import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, FolderOpen, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { useSite } from '../contexts/SiteContext';

// Strip HTML/Markdown markup to build a short plain-text preview.
function stripContent(text) {
  if (!text) return '';
  let s = text.replace(/\[solar-(?:dashboard|history)\]/gi, ' ');
  s = s.replace(/<[^>]+>/g, ' ');
  s = s
    .replace(/\[IMG:[^\]]+\]/g, '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^>\s+/gm, '')
    .replace(/^[\-\*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^(-{3,}|_{3,}|\*{3,})$/gm, '')
    .replace(/\|/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s;
}

function PageCard({ page }) {
  const full = stripContent(page.content);
  const preview = full.substring(0, 160);
  const cover = page.cover_image
    ? (page.cover_image.startsWith('/api')
        ? `${process.env.REACT_APP_BACKEND_URL}${page.cover_image}`
        : page.cover_image)
    : null;
  const updated = page.updated_at ? new Date(page.updated_at) : null;
  const showUpdated = updated && !isNaN(updated);
  const formattedUpdated = showUpdated
    ? updated.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <Link
      to={`/page/${page.slug}`}
      className="group block border border-border rounded-lg bg-card overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
      data-testid={`project-card-${page.slug}`}
    >
      {cover && (
        <div className="aspect-video overflow-hidden">
          <img
            src={cover}
            alt={page.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-6">
        <h3 className="text-xl font-semibold tracking-tight mb-2 group-hover:text-primary transition-colors flex items-center justify-between gap-2">
          {page.title}
          <ArrowRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
        </h3>
        {preview && (
          <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
            {preview}{full.length > 160 ? '…' : ''}
          </p>
        )}
        {showUpdated && (
          <div className="flex items-center text-xs text-muted-foreground">
            <Calendar className="h-3 w-3 mr-1" />
            Updated {formattedUpdated}
          </div>
        )}
      </div>
    </Link>
  );
}

function CardGrid({ pages }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {pages.map((p) => <PageCard key={p.slug} page={p} />)}
    </div>
  );
}

// Reusable grouped list of project pages. Mirrors the nav's group/section
// structure; ungrouped pages collect under a single "General" heading.
export function ProjectsList({ pages }) {
  const menuPages = Array.isArray(pages) ? pages : [];

  const blocks = [];
  const groupPos = {};
  const sectionPos = {};
  let generalBlock = null;

  menuPages.forEach((page) => {
    const group = (page.menu_group || '').trim();
    const section = (page.menu_section || '').trim();
    if (!group) {
      if (!generalBlock) {
        generalBlock = { type: 'general', label: 'General', pages: [] };
        blocks.push(generalBlock);
      }
      generalBlock.pages.push(page);
      return;
    }
    if (groupPos[group] === undefined) {
      groupPos[group] = blocks.length;
      blocks.push({ type: 'group', label: group, pages: [], sections: [] });
    }
    const groupBlock = blocks[groupPos[group]];
    if (!section) {
      groupBlock.pages.push(page);
      return;
    }
    const key = `${group}||${section}`;
    if (sectionPos[key] === undefined) {
      sectionPos[key] = groupBlock.sections.length;
      groupBlock.sections.push({ label: section, pages: [] });
    }
    groupBlock.sections[sectionPos[key]].pages.push(page);
  });

  // Hide the "General" heading when it's the only block (no real groups).
  const onlyGeneral = blocks.length === 1 && blocks[0].type === 'general';

  if (blocks.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="projects-empty">
        <p className="text-muted-foreground">No project pages yet. Check back soon!</p>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {blocks.map((block) => (
        <section key={block.label} data-testid={`project-group-${block.label.toLowerCase().replace(/\s+/g, '-')}`}>
          {!onlyGeneral && (
            <h3 className="text-xl font-semibold tracking-tight mb-6 pb-2 border-b border-border">
              {block.label}
            </h3>
          )}

          {block.pages.length > 0 && (
            <div className="mb-8">
              <CardGrid pages={block.pages} />
            </div>
          )}

          {block.type === 'group' && block.sections.map((sec) => (
            <div key={sec.label} className="mb-8" data-testid={`project-section-${sec.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <h4 className="text-lg font-semibold text-muted-foreground mb-4">{sec.label}</h4>
              <CardGrid pages={sec.pages} />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

export function ProjectsIndex() {
  const { pages } = useSite();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="projects-index">
      <SEO
        title="Browse Projects"
        description="Browse all of Greg's project pages — DIY, tech, and solar builds in one place."
        url="/projects"
      />

      <header className="mb-10 animate-fade-in">
        <div className="flex items-center gap-2 text-primary mb-3">
          <FolderOpen className="h-5 w-5" />
          <span className="text-xs tracking-[0.2em] uppercase font-mono font-medium">
            Projects
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-3">
          Browse Projects
        </h1>
        <p className="text-lg text-muted-foreground">
          Every project page in one place — explore the builds, write-ups, and dashboards.
        </p>
      </header>

      <ProjectsList pages={pages} />
    </div>
  );
}
